import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowLeft, Lock, Eye, Shield, Share2, UserCheck, Mail } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-blue-100 flex flex-col">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Link to="/">
                <img 
                  src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
                  alt="FaheemGPT Logo" 
                  className="w-10 h-10 rounded-lg object-cover" 
                />
            </Link>
            <span className="text-xl font-bold tracking-tight">FaheemGPT <span className="text-slate-400 font-normal">Intelligent Tutoring Platform</span></span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/">
                <Button variant="ghost" className="flex gap-2">
                    <ArrowLeft className="w-4 h-4" /> Back to Home
                </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1 py-16 px-6 bg-slate-50/50">
        <div className="max-w-4xl mx-auto">
            
            <div className="text-center mb-12 space-y-4">
                <div className="inline-flex items-center justify-center p-3 bg-green-50 rounded-xl mb-2">
                    <Lock className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="text-4xl font-bold text-slate-900">Privacy Policy</h1>
                <p className="text-slate-600 text-lg">Effective Date: October 26, 2025</p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                {/* Introduction */}
                <div className="p-8 border-b border-slate-100">
                    <p className="text-lg text-slate-600 leading-relaxed">
                        At FaheemGPT, we value your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you use our Intelligent Tutoring Platform.
                    </p>
                </div>

                <div className="divide-y divide-slate-100">
                    
                    {/* Section 1 */}
                    <div className="p-8 hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-start gap-4">
                            <div className="mt-1 p-2 bg-blue-100 rounded-lg">
                                <Eye className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="space-y-3">
                                <h3 className="text-xl font-bold text-slate-900">1. Information We Collect</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    We collect information to provide and improve our services, specifically:
                                </p>
                                <ul className="list-disc list-inside text-slate-600 space-y-2 pl-2">
                                    <li><strong>Account Information:</strong> Name, email address, and educational institution (if applicable).</li>
                                    <li><strong>Usage Data:</strong> Course progress, interaction logs with the AI tutor, and performance metrics.</li>
                                    <li><strong>Voice Data:</strong> Temporary audio recordings processed for speech-to-text functionality during sessions.</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Section 2 */}
                    <div className="p-8 hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-start gap-4">
                            <div className="mt-1 p-2 bg-purple-100 rounded-lg">
                                <Shield className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="space-y-3">
                                <h3 className="text-xl font-bold text-slate-900">2. How We Use Your Information</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    Your data is utilized to enhance your educational experience:
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    <Card className="bg-purple-50 border-purple-100 shadow-none">
                                        <CardContent className="p-4 text-sm text-purple-800 font-medium">
                                            Personalizing your learning path and curriculum.
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-purple-50 border-purple-100 shadow-none">
                                        <CardContent className="p-4 text-sm text-purple-800 font-medium">
                                            Improving the accuracy and responsiveness of our AI models.
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-purple-50 border-purple-100 shadow-none">
                                        <CardContent className="p-4 text-sm text-purple-800 font-medium">
                                            Providing technical support and responding to inquiries.
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-purple-50 border-purple-100 shadow-none">
                                        <CardContent className="p-4 text-sm text-purple-800 font-medium">
                                            Analyzing platform usage trends to enhance features.
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 3 */}
                    <div className="p-8 hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-start gap-4">
                            <div className="mt-1 p-2 bg-amber-100 rounded-lg">
                                <Lock className="w-5 h-5 text-amber-600" />
                            </div>
                            <div className="space-y-3">
                                <h3 className="text-xl font-bold text-slate-900">3. Data Security</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    We implement industry-standard security measures to protect your data from unauthorized access, alteration, or destruction. <span className="font-semibold text-slate-800">Audio data processed for voice interactions is ephemeral and not stored permanently on our servers.</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Section 4 */}
                    <div className="p-8 hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-start gap-4">
                            <div className="mt-1 p-2 bg-teal-100 rounded-lg">
                                <Share2 className="w-5 h-5 text-teal-600" />
                            </div>
                            <div className="space-y-3">
                                <h3 className="text-xl font-bold text-slate-900">4. Sharing Your Information</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    We do not sell your personal data. We may share data with:
                                </p>
                                <ul className="list-disc list-inside text-slate-600 space-y-2 pl-2">
                                    <li><strong>Educational Partners:</strong> If you are enrolled through a school or university, we may share progress reports with authorized educators.</li>
                                    <li><strong>Service Providers:</strong> Trusted third-party vendors who assist in platform hosting and maintenance (e.g., cloud infrastructure).</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Section 5 */}
                    <div className="p-8 hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-start gap-4">
                            <div className="mt-1 p-2 bg-indigo-100 rounded-lg">
                                <UserCheck className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div className="space-y-3">
                                <h3 className="text-xl font-bold text-slate-900">5. Your Rights</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    You have the right to access, correct, or delete your personal information. You may also opt-out of non-essential data collection. To exercise these rights, please contact our support team.
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
                
                {/* Footer of Card */}
                <div className="p-8 bg-slate-50 border-t border-slate-200 text-center">
                    <div className="inline-flex items-center justify-center p-3 bg-white rounded-full mb-4 shadow-sm">
                        <Mail className="w-6 h-6 text-slate-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Questions about your privacy?</h3>
                    <p className="text-slate-600 mb-6">Our Data Protection Officer is here to help.</p>
                    <a href="mailto:privacy@faheemgpt.com">
                        <Button className="bg-slate-900 text-white hover:bg-slate-800 px-8">Contact Privacy Team</Button>
                    </a>
                </div>
            </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6 text-slate-500 text-sm">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
              alt="FaheemGPT Logo" 
              className="w-8 h-8 rounded-lg object-cover" 
            />
             <span className="font-semibold text-slate-700">FaheemGPT Intelligent Tutoring Platform</span>
          </div>
          <div className="flex gap-8">
            <Link to="/privacy" className="hover:text-slate-900 font-medium text-slate-900">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-900">Terms</Link>
            <Link to="/support" className="hover:text-slate-900">Support</Link>
          </div>
          <div>
            &copy; 2024 FaheemGPT Inc. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
